import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const IDR_FIELDS = [
  ["standard_monthly_rate", "Standard / month"],
  ["standard_annual_rate", "Standard / year"],
  ["promo_monthly_rate", "1st-yr promo / month"],
  ["promo_annual_rate", "1st-yr promo / year"],
];
const USD_FIELDS = [
  ["standard_monthly_rate_usd", "Standard / month"],
  ["standard_annual_rate_usd", "Standard / year"],
  ["promo_monthly_rate_usd", "1st-yr promo / month"],
  ["promo_annual_rate_usd", "1st-yr promo / year"],
];
const RATE_FIELDS = [...IDR_FIELDS, ...USD_FIELDS];

export default function PricingPlans() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("platform.billing.manage");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/platform/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setRate = (idx, field, val) => setPlans((ps) => ps.map((p, i) => i === idx ? { ...p, [field]: val === "" ? null : Number(val) } : p));

  const save = async (plan) => {
    setSaving(plan.plan_id);
    try {
      const body = {};
      // Only entered values are sent — empty/null stays unpublished (TBA) in the DB.
      RATE_FIELDS.forEach(([f]) => { if (plan[f] != null) body[f] = plan[f]; });
      await api.patch(`/platform/plans/${plan.plan_id}`, body);
      toast.success(`${plan.name} rates saved`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 dark:text-ink-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-indigo-400 dark:text-ink-gold">Platform Management</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Pricing &amp; Plans</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">PETRINOS plan catalogue. Commercial rates are unpublished (TBD) until entered. 14-day free trial → first-year promo → standard rate.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {plans.map((p, idx) => {
          const published = p.standard_monthly_rate != null || p.standard_annual_rate != null;
          return (
            <div key={p.plan_id} data-testid={`plan-${p.plan_id}`} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-ink-line dark:bg-ink-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900 dark:text-ink-text">{p.name}</div>
                  <div className="text-sm text-slate-500">{p.description}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${published ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"}`} data-testid={`plan-price-state-${p.plan_id}`}>
                  {published ? "Published" : "TBD"}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                <span className="rounded bg-slate-100 px-2 py-0.5 dark:bg-ink-panel">{p.trial_days}-day trial</span>
                <span className="rounded bg-slate-100 px-2 py-0.5 dark:bg-ink-panel">{p.included_users != null ? `${p.included_users} users` : "Unlimited users"}</span>
                <span className="rounded bg-slate-100 px-2 py-0.5 dark:bg-ink-panel">{p.currency}</span>
              </div>
              <ul className="mt-3 space-y-1">
                {(p.features || []).map((f) => <li key={f} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"><Check className="h-3.5 w-3.5 text-emerald-500" />{f}</li>)}
              </ul>
              <div className="mt-4 space-y-3 border-t border-slate-100 pt-3 dark:border-ink-line">
                {[["IDR PRICING (Rp)", IDR_FIELDS, "Rp", "idr"], ["USD PRICING ($)", USD_FIELDS, "$", "usd"]].map(([section, fields, sym, key]) => (
                  <div key={key}>
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400" data-testid={`plan-${p.plan_id}-${key}-section`}>{section}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {fields.map(([field, label]) => (
                        <div key={field}>
                          <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">{label}</label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-mono-tf text-xs text-slate-400">{sym}</span>
                            <input type="number" min="0" disabled={!canManage} value={p[field] ?? ""} placeholder="TBA"
                              onChange={(e) => setRate(idx, field, e.target.value)} data-testid={`plan-${p.plan_id}-${field}`}
                              className="w-full rounded-md border border-slate-200 py-1.5 pl-8 pr-2 font-mono-tf text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text disabled:bg-slate-50" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {canManage && (
                <button onClick={() => save(p)} disabled={saving === p.plan_id} data-testid={`plan-save-${p.plan_id}`}
                  className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
                  {saving === p.plan_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rates
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
